const FilterTracker = () => {
  // this is a global filter tracker that will
  // be used to track the current filter state
  // in different pages and push it to the dataLayer

  const isDefined = window.filterTracker && window.searchTracker
  let currentFilters = []

  const trackFilterEvent = (name, value, searchTerm, type, eventSource) => {
    addNewFilter(name, value)

    window.dataLayer.push({
      event: 'GTMevent',
      event_name: 'filter_select',
      event_source: eventSource,
      event_data: {
        search_term: searchTerm,
        content_type: type,
        filter_name: name,
        filter_value: value,
      },
      _clear: true,
    })
  }

  const trackSearchEvent = (searchTerm, type) => {
    window.dataLayer.push({
      event: 'GTMevent',
      event_name: 'search_content',
      event_data: {
        search_term: searchTerm,
        content_type: type,
        filters_selected: getCurrentFilters(),
      },
      _clear: true,
    })
  }

  const trackSuggestion = (value, searchTerm) => {
    window.dataLayer.push({
      event: 'GTMevent',
      event_name: 'suggestion_click',
      event_data: {
        search_term: searchTerm,
        suggested_keyword: value,
        filters_selected: getCurrentFilters(),
      },
      _clear: true,
    })
  }

  const trackResetEvent = (searchTerm = '', currentModelsCount = 0) => {
    window.dataLayer.push({
      event: 'GTMevent',
      event_name: 'reset_click',
      event_data: {
        search_term: searchTerm,
        number_of_models_found: currentModelsCount,
        filters_selected: getCurrentFilters(),
      },
    })
  }

  const hasValidProps = (props) => {
    return Object.entries(props).length > 0
  }

  const addNewFilter = (name, value) => {
    const haveSameFilter = currentFilters
      .some(({ value: filterValue }) => filterValue === value)

    if (!haveSameFilter) {
      currentFilters.push({ name, value })
    }
  }

  const removeFilter = (props) => {
    // This function will remove from the currentFilters array,
    // if the value is not provided, it will remove all filters
    // if the value is provided, it will remove the filter with matching value
    // if the name is provided, it will remove filters with matching name

    const { name, value } = props || {}
    const bulkChecker = (filter) => {
      if (!name) { return false }

      return filter.name !== name
    }
    const checker = (filter) => {
      return filter.value !== value
    }

    const newFilters = currentFilters.filter(value ? checker: bulkChecker)

    currentFilters = newFilters
  }

  const getCurrentFilters = () => {
    const filters = currentFilters
      .filter(({ name }) => name !== 'keywords')
      .flatMap(({ name, value }) => `${name}=${value}`.replace(/ /g, '_')).join('/')

    return filters
  }

  if (!isDefined) {
    window.filterTracker = function (props = {}) {
      const {
        name,
        value,
        searchTerm,
        type,
        eventSource = '',
      } = props

      return hasValidProps(props)
        ? trackFilterEvent(name, value, searchTerm, type, eventSource)
        : null
    }

    window.searchTracker = function (props = {}) {
      const {
        searchTerm,
        type,
      } = props

      return hasValidProps(props)
        ? trackSearchEvent(searchTerm, type)
        : null
    }

    window.resetTracker = function (props = {}) {
      const {
        searchTerm,
        currentModelsCount,
      } = props

      return hasValidProps(props)
        ? trackResetEvent(searchTerm, currentModelsCount)
        : null
    }

    window.trackSuggestionClick = function (props = {}) {
      const {
        value,
        searchTerm,
      } = props

      return hasValidProps(props)
        ? trackSuggestion(value, searchTerm)
        : null
    }

    window.removeCurrentSearchFilter = removeFilter
  }
}

const CustomEventTracker = () => {
  const isDefined = window.customEventTracker

  if (!isDefined) {
    window.customEventTracker = function (props = {}) {
      if (Object.entries(props).length === 0) {
        return null
      }

      const {
        name,
        userId,
        eventData,
        customAttributes = {},
        eventCallback,
        eventTimeout,
      } = props

      window.dataLayer = window.dataLayer || []
      window.dataLayer.push({
        event: 'GTMevent',
        event_name: name,
        ...customAttributes,
        ...(userId && { user_id: userId }),
        event_data: eventData,
        ...(typeof eventCallback === 'function' && { eventCallback }),
        ...(typeof eventTimeout === 'number' && { eventTimeout }),
        _clear: true,
      })
    }
    if (typeof App !== 'undefined' && App.events) {
      App.events.publish('app:tracking:load')
    }
  }
}

const trackLinkClick = ({
  name,
  trackText,
  trackLabel,
  url = '',
  shouldRedirect = false,
}) => {
  window.customEventTracker({
    name,
    eventData: {
      element_url: url,
      element_text: trackText,
      element_label: trackLabel,
    },
  })

  if (url && shouldRedirect) {
    window.location.href = url
  }
}

const handleLinkClick = (event, eventName) => {
  const { target } = event || {}
  const { tagName, href, classList } = target || {}
  const trackLabel = target.getAttribute('data-track-label')
  const trackText = target.getAttribute('data-track-text')
  const avoidNavigation = target.getAttribute('data-avoid-navigation')
  const hasSpecialClass = classList.contains('js-track-link-click')
  const isAnchor = tagName === 'A'

  if ((isAnchor || hasSpecialClass) && trackLabel && trackText !== null) {
    event.preventDefault()
    trackLinkClick({
      name: eventName,
      trackText,
      trackLabel,
      url: href,
      shouldRedirect: !avoidNavigation,
    })
  }
}

const trackCtaClick = (event) => {
  const { target } = event || {}
  const { href } = target || {}
  const trackText = target.getAttribute('data-track-text')
  const hasTrackingText = trackText !== null || target.text !== null

  if (href && hasTrackingText) {
    event.preventDefault()
    event.stopPropagation()

    window.customEventTracker({
      name: 'cta_click',
      eventData: {
        element_url: href,
        element_text: trackText || target.text,
      },
    })

    window.location.href = href
  }
}

const FooterNavigationTracker = (containerId) => {
  const footerContainer = document.getElementById(containerId)

  if (footerContainer) {
    footerContainer.addEventListener('click', (e) => {
      handleLinkClick(e, 'footer_navigation')
    })
  }
}

const HeaderNavigationTracker = (containerId) => {
  const headerContainer = document.getElementById(containerId)

  if (headerContainer) {
    headerContainer.addEventListener('click', (e) => {
      handleLinkClick(e, 'header_navigation')
    })
  }
}

const CtaTracker = () => {
  document.addEventListener('click', (event) => {
    const targetClasses = event.target.classList

    if (targetClasses.contains('js-track-cta-click')) {
      return trackCtaClick(event)
    }

    return null
  })
}

const ViewItemListTracker = () => {
  const isDefined = window.trackViewItemList

  if (!isDefined) {
    window.trackViewItemList = (props = {}) => {
      if (Object.entries(props).length === 0) {
        return null
      }

      const {
        itemListId,
        itemListName,
        items,
        userId,
        eventSource,
      } = props

      if (!items || items.length === 0) {
        return null
      }

      window.dataLayer.push({
        event: 'view_item_list',
        user_id: userId || '',
        event_source: eventSource || '',
        ecommerce: {
          item_list_id: itemListId,
          item_list_name: itemListName,
          items,
        },
        _clear: true,
      })
    }
  }
}

const CtrImpressionsTracker = () => {
  if (window.postCtrImpressions) {
    return
  }

  window.postCtrImpressions = (itemIds, { perPage = 36 } = {}) => {
    if (!itemIds || itemIds.length === 0) {
      return
    }

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')

    if (!csrfToken) {
      return
    }

    const xhr = new XMLHttpRequest()
    xhr.open('POST', '/items/track-impressions', true)
    xhr.setRequestHeader('Content-Type', 'application/json')
    xhr.setRequestHeader('X-CSRF-Token', csrfToken)
    xhr.send(JSON.stringify({
      item_ids: itemIds,
      url: window.location.href,
      per_page: perPage,
    }))
  }
}

export {
  FilterTracker,
  CustomEventTracker,
  ViewItemListTracker,
  CtrImpressionsTracker,
  FooterNavigationTracker,
  HeaderNavigationTracker,
  CtaTracker,
}
